/* eslint-disable react/prop-types */

import { motion } from "framer-motion";
import { useState } from "react";
import { Calendar, Tag, Users, ArrowRight } from "lucide-react";
import { Modal } from "../Modal";
import logo from "../../../assets/logoBlack.png";

export function NewsEventCard({ item }) {
  const [open, setOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = item.image && !imageFailed;

  const formattedDate = item.date
    ? new Date(`${item.date}T00:00:00`).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "";

  const chips = (
    <div className="flex flex-wrap gap-2">
      {item.category && (
        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-blue-700 rounded-full bg-blue-500/10">
          <Tag size={14} />
          {item.category}
        </span>
      )}
      {item.audience.map((audience) => (
        <span
          key={audience}
          className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-green-700 rounded-full bg-green-500/10"
        >
          <Users size={14} />
          {audience}
        </span>
      ))}
    </div>
  );

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="relative flex flex-col h-full overflow-hidden transition-all duration-500 border shadow-lg group bg-white/80 backdrop-blur-lg rounded-2xl border-white/20 hover:shadow-xl"
      >
        <div className="relative aspect-[4/3] bg-gradient-to-br from-primary-50 to-secondary-50">
          {showImage ? (
            <>
              {!imageLoaded && (
                <motion.div
                  initial={{ opacity: 0.5 }}
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200"
                />
              )}
              <img
                src={item.image}
                alt={item.title}
                className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${
                  imageLoaded ? "opacity-100" : "opacity-0"
                }`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageFailed(true)}
              />
            </>
          ) : (
            // Every live item currently has image: null — keep the card rhythm with a watermark
            <div className="absolute inset-0 flex items-center justify-center">
              <img src={logo} alt="" className="w-24 opacity-20" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </div>

        <div className="relative flex flex-col flex-1 p-6">
          <div className="flex items-center gap-2 mb-3 text-sm text-gray-400">
            <Calendar size={16} className="text-blue-400" />
            <span>{formattedDate}</span>
          </div>

          <h3 className="mb-3 text-2xl font-bold text-transparent bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text line-clamp-2">
            {item.title}
          </h3>

          <div className="mb-4">{chips}</div>

          <p className="leading-relaxed text-gray-600 whitespace-pre-line line-clamp-3">
            {item.description}
          </p>

          <motion.button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 pt-4 mt-auto mr-auto font-medium transition-colors text-primary-600 hover:text-primary-700"
            whileHover={{ x: 5 }}
          >
            Read more
            <ArrowRight size={16} />
          </motion.button>
        </div>
      </motion.div>

      <Modal isOpen={open} onClose={() => setOpen(false)} className="max-w-3xl" label={item.title}>
        {showImage && (
          <img
            src={item.image}
            alt={item.title}
            className="object-cover w-full mb-6 max-h-80 rounded-xl"
          />
        )}

        <div className="flex items-center gap-2 mb-3 text-sm text-gray-500">
          <Calendar size={16} className="text-blue-400" />
          <span>{formattedDate}</span>
        </div>

        <h2 className="mb-4 text-2xl font-bold text-gray-900 md:text-3xl pr-10">
          {item.title}
        </h2>

        <div className="mb-6">{chips}</div>

        <p className="leading-relaxed text-gray-700 whitespace-pre-line">
          {item.description}
        </p>
      </Modal>
    </>
  );
}
